import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { apiError } from "../lib/api";
import { Check, X, Inbox } from "lucide-react";

const TABS = [
  { value: "pending", label: "In attesa" },
  { value: "approved", label: "Approvati" },
  { value: "rejected", label: "Rifiutati" },
];

export default function AdminApprovals() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [workingId, setWorkingId] = useState(null);

  const load = useCallback(async (status) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/agency-requests`, {
        params: { status },
      });
      setItems(data.items || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(tab);
  }, [tab, load]);

  const act = async (id, action) => {
    setWorkingId(id);
    try {
      await api.post(`/admin/agency-requests/${id}`, { action });
      toast.success(action === "approve" ? "Richiesta approvata" : "Richiesta rifiutata");
      await load(tab);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setWorkingId(null);
    }
  };

  return (
    <div data-testid="admin-approvals-page" className="space-y-6 fade-up">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-[var(--mp-muted)]">
          Amministrazione
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">
          Richieste di accesso agenzia
        </h1>
        <p className="mt-3 text-[var(--mp-muted)] max-w-2xl">
          Approva i collaboratori che devono entrare nell'area agenzia oppure rifiuta le richieste
          non valide.
        </p>
      </div>

      <div className="flex items-center gap-2 rounded-full bg-white border border-[var(--mp-border)] p-1 w-fit">
        {TABS.map((t) => (
          <button
            key={t.value}
            data-testid={`tab-${t.value}`}
            onClick={() => setTab(t.value)}
            className={`px-4 h-9 rounded-full text-sm font-medium transition-colors ${
              tab === t.value
                ? "bg-[var(--mp-ink)] text-white"
                : "text-[var(--mp-muted)] hover:text-[var(--mp-ink)]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="rounded-3xl border border-[var(--mp-border)] bg-white overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[var(--mp-muted)]">Caricamento…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-[var(--mp-muted)] flex flex-col items-center gap-3">
            <Inbox className="h-6 w-6" />
            Nessuna richiesta {tab === "pending" ? "in attesa" : tab === "approved" ? "approvata" : "rifiutata"}.
          </div>
        ) : (
          <ul className="divide-y divide-[var(--mp-border)]">
            {items.map((u) => (
              <li
                key={u.id}
                data-testid={`request-row-${u.id}`}
                className="flex flex-col sm:flex-row sm:items-center gap-4 p-5"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg tracking-tight truncate">
                    {u.full_name || u.email}
                  </div>
                  <div className="text-sm text-[var(--mp-muted)] truncate">{u.email}</div>
                  {u.created_at && (
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--mp-muted)] mt-1">
                      Inviata il {new Date(u.created_at).toLocaleString("it-IT")}
                    </div>
                  )}
                </div>
                <StatusBadge status={u.status} />
                {tab === "pending" && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      size="sm"
                      data-testid={`approve-${u.id}`}
                      disabled={workingId === u.id}
                      onClick={() => act(u.id, "approve")}
                      className="rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
                    >
                      <Check className="h-4 w-4 mr-1" /> Approva
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`reject-${u.id}`}
                      disabled={workingId === u.id}
                      onClick={() => act(u.id, "reject")}
                      className="rounded-full border-[var(--mp-ink)]"
                    >
                      <X className="h-4 w-4 mr-1" /> Rifiuta
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: "bg-[var(--mp-yellow)] text-[var(--mp-ink)]",
    approved: "bg-[var(--mp-ink)] text-white",
    rejected: "bg-[var(--mp-primary)] text-white",
  };
  const label = {
    pending: "In attesa",
    approved: "Approvato",
    rejected: "Rifiutato",
  };
  return (
    <Badge
      className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] ${map[status] || ""}`}
    >
      {label[status] || status}
    </Badge>
  );
}
